/**
 * Paket Bilgileri (Koli / Desi) — eski `products_single.php` "Paket Bilgileri"
 * bloğunun karşılığı.
 *
 * Eski sistemin düzeltilen hataları:
 *  · Desi ÜÇ ayrı yerde, İKİ farklı yuvarlamayla hesaplanıyordu: ekranda
 *    `Math.ceil`, maliyet sorgusunda SQL `ROUND()`. 50×40×32 cm koli ekranda 22,
 *    maliyette 21 desi çıkıyordu → doğrudan fiyat hatası.
 *    Burada desi arayüzde HİÇ HESAPLANMAZ; tek kaynak `previewDesi` callable'ıdır
 *    (sunucudaki `computeDesi`, gerçek veriye karşı 371/371 doğrulanmıştır).
 *  · Ürünün kolisi yoksa `fetchTotalCargoDeci` hata fırlatıyor ve maliyet satırı
 *    hiç görünmüyordu — kullanıcıya sebep söylenmiyordu. Artık uyarı gösterilir.
 *  · Koli tespiti `typeofproduct = 2` sabitine gömülüydü. Artık `isPackaging`
 *    bayrağı.
 */

import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { where, orderBy } from "firebase/firestore";
import { PageHeader, Modal, Spinner, EmptyState, useToast } from "../components";
import { useCollection, useDoc, useCallableAction } from "../hooks";
import { useCan } from "../lib/auth";
import { tl, n } from "../lib/format";
import { DecimalInput, ErrorNote, Field } from "../components";
import type { ComponentDoc, UpsertComponentInput, UpsertComponentOutput } from "../lib/types";
import { parseDecimal } from "../lib/decimal";
import type { ProductDoc } from "./Products";
import { packagingPath, type RecipeLine, type SaveRecipeLineInput, type SaveRecipeLineOutput, type DeleteRecipeLineInput } from "./Recipe";

/** Desi sunucudan gelir. Arayüzde bölme/yuvarlama YAPILMAZ. */
export interface PreviewDesiInput {
  /** Kayıtlı koli satırlarının desisi için. */
  productId?: string;
  /** Kaydetmeden önizleme için (yeni koli ölçüleri). Ölçüler SANTİMETRE. */
  boxes?: Array<{ height: number; width: number; length: number; quantity: number }>;
}

export interface PreviewDesiOutput {
  desi: number;
  /** KURUŞ. Yalnızca costView olan çağırana doldurulur. */
  cargoKurus: number | null;
  lines: Array<{ index: number; unitDesi: number; quantity: number; lineDesi: number }>;
  warnings: string[];
}

const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

// ═══════════════════════════════════════════════════════════════════════════
// ROTA SAYFASI
// ═══════════════════════════════════════════════════════════════════════════

export default function PackagingPage() {
  const { productId: routeId } = useParams<{ productId: string }>();
  const productId = routeId ?? "";
  const { data: product, loading } = useDoc<ProductDoc>(`products/${productId}`);

  return (
    <PageHeader
      title={product ? `Paket Bilgileri — ${product.name}` : "Paket Bilgileri"}
    >
      {loading ? <Spinner /> : null}
      {!loading && !product ? (
        <EmptyState title="Ürün bulunamadı" description="Bağlantı geçersiz olabilir." />
      ) : null}
      {product ? (
        <>
          <Link
            to={`/products/${productId}`}
            className="mb-4 inline-block text-sm text-sky-700 hover:underline"
          >
            ← Ürün kartına dön
          </Link>
          <PackagingBlock productId={productId} />
        </>
      ) : null}
    </PageHeader>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAKET BLOĞU — ürün kartına da gömülür
// ═══════════════════════════════════════════════════════════════════════════

export function PackagingBlock({ productId }: { productId: string }) {
  const can = useCan();
  const toast = useToast();
  const readOnly = !can.isStaff;

  const { data: lines, loading, error } = useCollection<RecipeLine>(packagingPath(productId), [
    where("deleted", "==", false),
    orderBy("order"),
  ]);

  const remove = useCallableAction<DeleteRecipeLineInput, { ok: boolean }>("deleteRecipeLine");
  const desiPreview = useCallableAction<PreviewDesiInput, PreviewDesiOutput>("previewDesi");

  const [totals, setTotals] = useState<PreviewDesiOutput | null>(null);
  const [adding, setAdding] = useState(false);

  // Desi/kargo toplamı satır listesi değiştikçe SUNUCUDAN tazelenir.
  // İmza olarak satır kimlik+adet birleşimi kullanılır; gereksiz çağrı olmasın.
  const signature = useMemo(
    () => lines.map((l) => `${l.id}:${l.quantity}`).join("|"),
    [lines],
  );

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    void desiPreview.run({ productId }).then((res) => {
      if (!cancelled && res) setTotals(res);
    });
    return () => {
      cancelled = true;
    };
    // `desiPreview` her render'da yeni referans olabilir; bilinçli olarak
    // yalnızca ürün ve satır imzasına bağlıyız.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, signature]);

  const lineDesi = useMemo(() => {
    const map = new Map<number, number>();
    for (const l of totals?.lines ?? []) map.set(l.index, l.unitDesi);
    return map;
  }, [totals]);

  return (
    <section>
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Paket Bilgileri</h3>
          <p className="text-xs text-slate-500">
            Buraya <strong>ölçülü koliler</strong> girilir; maliyete birim fiyatla değil{" "}
            <strong>desi × desi başı kargo ücreti</strong> olarak girer ve stoktan düşmez.
          </p>
          {/* ★ Z-KARTON ve POŞET buraya girilmişti ve maliyette HİÇ görünmüyordu:
              m² sargının boyu/eni olmadığı için desiye giremiyor, ambalaj
              koleksiyonu da malzemeye sayılmıyordu. 825 satır, 413 ürün, ürün
              başına ~200 ₺ görünmeyen gider. Satırlar reçeteye taşındı; kutu
              artık nereye ne gireceğini SÖYLÜYOR. */}
          <p className="mt-1 text-xs text-slate-500">
            Karton ve poşet gibi <strong>m² ile ölçülen sargılar</strong> buraya değil{" "}
            <strong>Reçete sekmesine</strong> girilir — onlar kargo değil malzeme giderdir.
          </p>
        </div>
        {!readOnly ? (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Koli Ekle
          </button>
        ) : null}
      </header>

      {readOnly ? (
        <p className="mb-3 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Üretim rolünde paket bilgileri <strong>salt okunurdur</strong>.
        </p>
      ) : null}

      <ErrorNote error={error ?? remove.error ?? desiPreview.error} />
      {loading ? <Spinner /> : null}

      {!loading && lines.length === 0 ? (
        <EmptyState
          title="Koli tanımlanmamış"
          description="Kolisi olmayan üründe kargo maliyeti 0 kabul edilir; maliyet ve kâr rakamı eksik veriyle hesaplanır."
        />
      ) : null}

      {lines.length > 0 ? (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Koli</th>
                <th className="px-3 py-2 text-right">Ölçü Y×G×U (cm)</th>
                <th className="px-3 py-2 text-right">Birim Desi</th>
                <th className="px-3 py-2 text-right">Adet</th>
                <th className="px-3 py-2 text-right">Satır Desi</th>
                {!readOnly ? <th className="px-3 py-2 text-right">İşlem</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.map((l, i) => {
                const s = l.snapshot ?? {};
                /**
                 * ★ ÖLÇÜ ADRESİ `snapshot.dimensions.*`, DÜZ `snapshot.height` DEĞİL.
                 *
                 * Bu sütun düz adları okuyordu; anlık görüntüyü yazan
                 * `catalog/recipe.ts:175-181` ise ölçüleri `dimensions` nesnesinin
                 * içine koyuyor. Canlı ölçüm: 279 paket satırının 279'unda
                 * `dimensions.*` dolu, düz adlar 0/279 → "Ölçü Y×G×U" sütunu
                 * istisnasız "—" gösteriyordu. Hata sessizdi çünkü eksik alan
                 * `undefined` döner, `undefined` da "ölçü girilmemiş" ile aynı
                 * görünür. Aynı dosyanın 431-432. satırları BİLEŞEN dokümanını
                 * doğru adresten (`dimensions`) okuyor — iki okuma birbiriyle
                 * çelişiyordu.
                 *
                 * Düz adlar yedek olarak kalır (göç kayıtları taşıyabilir).
                 */
                const dimH = s.dimensions?.height ?? s.height;
                const dimW = s.dimensions?.width ?? s.width;
                const dimL = s.dimensions?.length ?? s.length;
                const unit = lineDesi.get(i);
                return (
                  <tr key={l.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-800">{s.name ?? l.componentId}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {dimH || dimW || dimL
                        ? `${n(dimH ?? 0)}×${n(dimW ?? 0)}×${n(dimL ?? 0)}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {unit === undefined ? "—" : n(unit)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{n(l.quantity)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {unit === undefined ? "—" : n(unit * l.quantity)}
                    </td>
                    {!readOnly ? (
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          onClick={() => void remove.run({ productId, lineId: l.id })}
                        >
                          Çıkar
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-sm font-semibold text-slate-700">
                  Toplam Desi
                </td>
                <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                  {totals ? n(totals.desi) : desiPreview.loading ? "…" : "—"}
                </td>
                {!readOnly ? <td /> : null}
              </tr>
              {/* Kargo tutarı bir MALİYET kalemidir → yalnızca costView. */}
              {can.seeCost ? (
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-right text-sm text-slate-600">
                    Kargo Maliyeti
                  </td>
                  <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                    {totals?.cargoKurus === null || totals?.cargoKurus === undefined
                      ? "—"
                      : tl(totals.cargoKurus)}
                  </td>
                  {!readOnly ? <td /> : null}
                </tr>
              ) : null}
            </tfoot>
          </table>
        </div>
      ) : null}

      {/* Sessiz atlama yok: eksik ölçü / eksik adet uyarıları aynen gösterilir. */}
      {totals && totals.warnings.length > 0 ? (
        <ul className="mt-3 space-y-1 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {totals.warnings.map((w) => (
            <li key={w}>• {w}</li>
          ))}
        </ul>
      ) : null}

      {adding ? (
        <AddPackagingModal
          productId={productId}
          nextOrder={(lines[lines.length - 1]?.order ?? 0) + 10}
          onClose={() => setAdding(false)}
          onSaved={() => {
            toast.success("Koli eklendi.");
            setAdding(false);
          }}
        />
      ) : null}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// KOLİ EKLEME — mevcut koli seç VEYA yeni koli tanımla
// ═══════════════════════════════════════════════════════════════════════════

function AddPackagingModal({
  productId,
  nextOrder,
  onClose,
  onSaved,
}: {
  productId: string;
  nextOrder: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data: components } = useCollection<ComponentDoc>("components", [
    where("deleted", "==", false),
    where("isPackaging", "==", true),
    orderBy("nameLower"),
  ]);

  const upsert = useCallableAction<UpsertComponentInput, UpsertComponentOutput>("upsertComponent");
  const saveLine = useCallableAction<SaveRecipeLineInput, SaveRecipeLineOutput>("saveRecipeLine");
  const desiPreview = useCallableAction<PreviewDesiInput, PreviewDesiOutput>("previewDesi");

  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [componentId, setComponentId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [localError, setLocalError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [height, setHeight] = useState("");
  const [width, setWidth] = useState("");
  const [length, setLength] = useState("");
  const [preview, setPreview] = useState<PreviewDesiOutput | null>(null);

  const selected = components.find((c) => c.id === componentId);
  const activeComponents = components.filter((c) => c.active !== false);

  /** Desi ÖNİZLEMESİ de sunucudan gelir — istemcide formül yok. */
  async function askDesi() {
    setLocalError(null);
    const dims = {
      height: parseDecimal(height) ?? 0,
      width: parseDecimal(width) ?? 0,
      length: parseDecimal(length) ?? 0,
    };
    const qty = parseDecimal(quantity) ?? 0;
    if (dims.height <= 0 || dims.width <= 0 || dims.length <= 0) {
      setLocalError("Desi önizlemesi için üç ölçü de sıfırdan büyük olmalıdır (cm).");
      return;
    }
    const res = await desiPreview.run({ boxes: [{ ...dims, quantity: qty > 0 ? qty : 1 }] });
    if (res) setPreview(res);
  }

  async function submit() {
    setLocalError(null);
    const qty = parseDecimal(quantity);
    if (qty === null || qty <= 0) {
      setLocalError("Adet sıfırdan büyük olmalıdır.");
      return;
    }

    let targetId = componentId;

    if (mode === "new") {
      if (!name.trim()) {
        setLocalError("Koli adı zorunludur.");
        return;
      }
      const dims = {
        height: parseDecimal(height) ?? 0,
        width: parseDecimal(width) ?? 0,
        length: parseDecimal(length) ?? 0,
      };
      if (dims.height <= 0 || dims.width <= 0 || dims.length <= 0) {
        setLocalError("Koli ölçüleri (cm) sıfırdan büyük olmalıdır; desi bu ölçülerden hesaplanır.");
        return;
      }
      const created = await upsert.run({
        name: name.trim(),
        // Koli kategorisi sunucuda `isPackaging` bayraklı kategoriye eşlenir;
        // eski sistemdeki sabit "kategori 2" varsayımı kaldırıldı.
        categoryId: "",
        unit: "adet",
        dimensions: dims,
        active: true,
        isPackaging: true,
        // Koliler reçete bileşeni seçicisinde çıkmaz — buradan eklenirler.
        isSelectableAsComponent: false,
      });
      if (!created) return;
      targetId = created.componentId;
    } else if (!targetId) {
      setLocalError("Koli seçilmelidir.");
      return;
    }

    const res = await saveLine.run({
      productId,
      type: "packaging",
      componentId: targetId,
      quantity: qty,
      explanation: "",
      order: nextOrder,
    });
    if (res) onSaved();
  }

  return (
    <Modal open onClose={onClose} title="Koli Ekle" size="lg">
      <div className="mb-4 flex gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={mode === "existing"}
            onChange={() => setMode("existing")}
          />
          Mevcut koliyi seç
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" checked={mode === "new"} onChange={() => setMode("new")} />
          Yeni koli tanımla
        </label>
      </div>

      {mode === "existing" ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Koli">
            <select
              className={INPUT}
              value={componentId}
              onChange={(e) => setComponentId(e.target.value)}
            >
              <option value="">Koli Seç…</option>
              {activeComponents.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Adet (1 mamul için)">
            <DecimalInput value={quantity} onChange={setQuantity} />
          </Field>
          {/* ★ m² sargının burada işi yok: ölçüsü olmadığı için desiye giremez.
              Uyarı seçim ANINDA çıkar — kaydettikten sonra "neden desi 0?" diye
              aramak zorunda kalınmasın. */}
          {selected?.unit === "m2" ? (
            <p className="md:col-span-2 rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <strong>Bu bir koli değil, m² ile ölçülen sargı.</strong> Ölçüsü olmadığı
              için desiye giremez; kargo maliyetine katkısı olmaz.{" "}
              <strong>Reçete sekmesine</strong> ekleyin — orada malzeme gideri olarak sayılır.
            </p>
          ) : selected ? (
            <p className="md:col-span-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Ölçü: {n(selected.dimensions?.height ?? 0)}×{n(selected.dimensions?.width ?? 0)}×
              {n(selected.dimensions?.length ?? 0)} cm · Birim desi:{" "}
              {selected.deci ? n(selected.deci) : "sunucuda hesaplanacak"}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Field label="Koli Adı">
              <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          <Field label="Yükseklik (cm)">
            <DecimalInput value={height} onChange={setHeight} />
          </Field>
          <Field label="Genişlik (cm)">
            <DecimalInput value={width} onChange={setWidth} />
          </Field>
          <Field label="Uzunluk (cm)">
            <DecimalInput value={length} onChange={setLength} />
          </Field>
          <Field label="Adet (1 mamul için)">
            <DecimalInput value={quantity} onChange={setQuantity} />
          </Field>

          <div className="md:col-span-2 flex items-center gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2">
            <button
              type="button"
              onClick={askDesi}
              disabled={desiPreview.loading}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-100 disabled:opacity-50"
            >
              {desiPreview.loading ? "Hesaplanıyor…" : "Desi Önizle"}
            </button>
            <span className="text-xs text-slate-600">
              {preview
                ? `Toplam ${n(preview.desi)} desi`
                : "Desi sunucuda hesaplanır — arayüz formül çalıştırmaz."}
            </span>
          </div>

          {preview && preview.warnings.length > 0 ? (
            <ul className="md:col-span-2 space-y-1 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {preview.warnings.map((w) => (
                <li key={w}>• {w}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      <ErrorNote error={localError ?? upsert.error ?? saveLine.error} />

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded border border-slate-300 px-4 py-2 text-sm">
          Vazgeç
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={upsert.loading || saveLine.loading}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {upsert.loading || saveLine.loading ? "Kaydediliyor…" : "Ekle"}
        </button>
      </div>
    </Modal>
  );
}

/** Ürün kartında kullanılan sade özet: yalnızca toplam desi + (yetkiliyse) kargo. */
export function DesiSummary({ productId }: { productId: string }) {
  const can = useCan();
  const desiPreview = useCallableAction<PreviewDesiInput, PreviewDesiOutput>("previewDesi");
  const [totals, setTotals] = useState<PreviewDesiOutput | null>(null);

  useEffect(() => {
    let cancelled = false;
    void desiPreview.run({ productId }).then((res) => {
      if (!cancelled && res) setTotals(res);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  if (desiPreview.error) return <ErrorNote error={desiPreview.error} />;
  if (!totals) return <span className="text-sm text-slate-600">Desi hesaplanıyor…</span>;

  return (
    <span className="text-sm text-slate-700">
      <strong className="tabular-nums">{n(totals.desi)}</strong> desi
      {can.seeCost && totals.cargoKurus !== null ? (
        <>
          <span className="mx-2 text-slate-300">·</span>
          Kargo <strong className="tabular-nums">{tl(totals.cargoKurus)}</strong>
        </>
      ) : null}
    </span>
  );
}
